import { useCallback, useEffect, useRef, useState } from "react";
import {
  Banner,
  Button,
  Card,
  LegacyStack,
  Select,
  Text,
  TextField,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import {
  nightStep,
  uploadTransportDocument,
  getTransportDocuments,
} from "services/orders.service";
import { directionKey } from "utils/helperFunctions";

interface TransportDocument {
  id: number;
  file: string;
  doc_kind: string;
  created: string;
}

interface NightActionsCardProps {
  id: string;
  type: string;
  night_mode?: boolean;
  night_ready?: boolean;
  night_step?: string | null;
  night_docs?: number;
  gate_exit_at?: string | null;
  onRefresh: () => Promise<void> | void;
}

const ZONE_OPTIONS = ["A", "B", "C", "D", "E"].map((zone) => ({
  label: zone,
  value: zone,
}));

/**
 * The dispatcher went home; while night mode is on, the guard drives the
 * truck through whatever step it is standing on and finally releases it.
 * Renders nothing at all outside night mode - this card must never surface
 * a way to run the yard flow during the day.
 */
const NightActionsCard: React.FC<NightActionsCardProps> = ({
  id,
  type,
  night_mode = false,
  night_ready = false,
  night_step = null,
  night_docs = 0,
  gate_exit_at = null,
  onRefresh,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [zone, setZone] = useState("A");
  const [places, setPlaces] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingMsg, setPendingMsg] = useState<string | null>(null);
  const [documents, setDocuments] = useState<TransportDocument[]>([]);
  const [documentsLoaded, setDocumentsLoaded] = useState(false);

  // photo_documents always needs a photo; ready_to_leave only needs one when
  // none has been attached yet (e.g. an UPLOAD truck with no dedicated step).
  const needsDocumentSection =
    night_step === "photo_documents" ||
    (night_step === "ready_to_leave" && night_docs === 0);

  const loadDocuments = useCallback(async () => {
    if (!needsDocumentSection) return;
    try {
      const res = await getTransportDocuments(id, type);
      setDocuments(res?.documents || []);
    } catch (e) {
      // A listing failure should not block the photograph button itself.
    } finally {
      setDocumentsLoaded(true);
    }
  }, [id, type, needsDocumentSection]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  // Fresh step, fresh messages - a stale error/pending banner from the
  // previous step must not carry over once the card advances.
  useEffect(() => {
    setError(null);
    setPendingMsg(null);
    setPlaces("");
  }, [night_step]);

  if (!night_mode || gate_exit_at) return null;

  if (!night_ready) {
    return (
      <Card>
        <LegacyStack vertical spacing="tight">
          <Text as="h3" variant="headingMd">
            {t("night_actions_title")}
          </Text>
          <Banner tone="warning">{t("night_assign_number_first")}</Banner>
        </LegacyStack>
      </Card>
    );
  }

  // null means the chain is finished - the existing release card takes over.
  if (night_step === null) return null;

  const documentsCount = documentsLoaded ? documents.length : night_docs;
  const confirmDisabledByDocs = needsDocumentSection && documentsCount === 0;

  const handleFileChosen = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError(null);
    setUploading(true);
    try {
      const res = await uploadTransportDocument(file, id, type);
      if (res?.success) {
        await loadDocuments();
      } else {
        setError(
          res?.error === "file_too_large"
            ? t("document_too_large")
            : res?.error === "unsupported_file_type"
            ? t("document_unsupported_type")
            : t("document_upload_failed")
        );
      }
    } catch (e) {
      setError(t("document_upload_failed"));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const runStep = async (extra?: Record<string, string>) => {
    if (!night_step) return;
    setError(null);
    setPendingMsg(null);
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("id_transport", id);
      formData.append("type", type);
      // step and expected are both required and must match what the guard
      // saw - a mismatch means somebody else already moved this truck.
      formData.append("step", night_step);
      formData.append("expected", night_step);
      if (extra) {
        Object.entries(extra).forEach(([key, value]) => formData.append(key, value));
      }

      const res = await nightStep(formData);

      if (res?.pending) {
        // Not an error - the weighbridge just hasn't settled, let them retry.
        setPendingMsg(t("night_scale_pending"));
        return;
      }

      if (res?.success === false) {
        setError(res?.message || res?.error || t("please_try_again"));
        await onRefresh();
        return;
      }

      // Advanced to the next step - the parent re-fetches and hands this
      // card fresh props so it re-renders on the new night_step.
      await onRefresh();
    } catch (e) {
      setError(t("please_try_again"));
      await onRefresh();
    } finally {
      setLoading(false);
    }
  };

  const stepLabel = t(directionKey(night_step, type));

  const documentSection = needsDocumentSection && (
    <LegacyStack vertical spacing="tight">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,application/pdf"
        capture="environment"
        style={{ display: "none" }}
        onChange={handleFileChosen}
      />
      <Button
        size="large"
        fullWidth
        loading={uploading}
        disabled={uploading || loading}
        onClick={() => fileInputRef.current?.click()}
      >
        {t("night_photo_document")}
      </Button>
      <Text as="p" variant="bodyMd">
        {t("night_documents_attached")}: {documentsLoaded ? documentsCount : "…"}
      </Text>
      {confirmDisabledByDocs && (
        <Banner tone="warning">{t("night_document_required")}</Banner>
      )}
    </LegacyStack>
  );

  let controls: React.ReactNode;
  if (night_step === "assign_zone") {
    controls = (
      <LegacyStack vertical spacing="tight">
        <Select
          label={t("night_zone")}
          options={ZONE_OPTIONS}
          value={zone}
          onChange={setZone}
          disabled={loading}
        />
        <Button
          variant="primary"
          size="large"
          loading={loading}
          disabled={loading}
          onClick={() => runStep({ terminal_zone: zone })}
        >
          {t("night_confirm_step")}
        </Button>
      </LegacyStack>
    );
  } else if (night_step === "finish_uploading") {
    controls = (
      <LegacyStack vertical spacing="tight">
        <TextField
          label={t("night_places")}
          autoComplete="off"
          value={places}
          onChange={(value) => {
            if (/^\d*$/.test(value) && (value === "" || Number(value) <= 32767)) {
              setPlaces(value);
            }
          }}
          disabled={loading}
        />
        <Button
          variant="primary"
          size="large"
          loading={loading}
          disabled={loading || places === ""}
          onClick={() => runStep({ loaded_qty: places })}
        >
          {t("night_confirm_step")}
        </Button>
      </LegacyStack>
    );
  } else if (needsDocumentSection) {
    controls = (
      <LegacyStack vertical spacing="tight">
        {documentSection}
        <Button
          variant="primary"
          size="large"
          loading={loading}
          disabled={loading || uploading || confirmDisabledByDocs}
          onClick={() => runStep()}
        >
          {t("night_confirm_step")}
        </Button>
      </LegacyStack>
    );
  } else if (night_step === "start_weighing" || night_step === "start_weighing_again") {
    controls = (
      <Button
        variant="primary"
        size="large"
        loading={loading}
        disabled={loading}
        onClick={() => runStep()}
      >
        {t("night_fix_weight")}
      </Button>
    );
  } else {
    controls = (
      <Button
        variant="primary"
        size="large"
        loading={loading}
        disabled={loading}
        onClick={() => runStep()}
      >
        {t("night_confirm_step")}
      </Button>
    );
  }

  return (
    <Card>
      <LegacyStack vertical spacing="tight">
        <Text as="h3" variant="headingMd">
          {t("night_actions_title")}
        </Text>
        <Text as="p" variant="bodyMd">
          {stepLabel}
        </Text>
        {error && <Banner tone="critical">{error}</Banner>}
        {pendingMsg && <Banner tone="info">{pendingMsg}</Banner>}
        {controls}
      </LegacyStack>
    </Card>
  );
};

export default NightActionsCard;
